import { useState, useEffect } from 'react';
import useCurrentUser from '../../hooks/useCurrentUser';
import PatchNotesModal from './PatchNotesModal';
import useAcl from '../../hooks/useAcl';
import api from '../../services/api';
import useAgence from '../../hooks/useAgence';

const menuGroups = [
    {
        label: 'Planning',
        items: [
            { id: 'planning', icon: 'ti-calendar', label: 'Planning', permission: 'voir_planning' },
        ]
    },
    {
        label: 'Patients',
        items: [
            { id: 'patients',    icon: 'ti-stethoscope', label: 'Patients',      permission: 'voir_patients' },
            { id: 'recurrences', icon: 'ti-repeat',       label: 'Récurrences',   permission: 'voir_recurrences' },
            { id: 'map',         icon: 'ti-map-pin',      label: 'Carte patients', permission: 'voir_carte' },
        ]
    },
    {
        label: 'Administration',
        superAdminOnly: true,
        items: [
            { id: 'users', icon: 'ti-users', label: 'Utilisateurs', superAdminOnly: true },
            { id: 'secteurs', icon: 'ti-map', label: 'Secteurs' },
            { id: 'agences', icon: 'ti-building', label: 'Agences', superAdminOnly: true },
            { id: 'acl', icon: 'ti-shield-lock', label: 'Permissions', superAdminOnly: true },
            { id: 'bug-reports', icon: 'ti-bug', label: 'Rapports bugs', superAdminOnly: true },
        ]
    },
    {
        label: 'Rapports',
        items: [
            { id: 'stats', icon: 'ti-chart-bar',      label: 'Statistiques', permission: 'voir_stats' },
            { id: 'logs',  icon: 'ti-clipboard-list', label: 'Logs',         permission: 'voir_logs' },
        ]
    },
];

export default function Sidebar({ activePage, onNavigate, onLogout, checkAndUnlock }) {
    const [expanded, setExpanded] = useState(true);
    const [showPatchNotes, setShowPatchNotes] = useState(false);
    const currentUser = useCurrentUser();
    const [appVersion, setAppVersion] = useState('');
    const [bugCount, setBugCount] = useState(0);
    const { can, aclLoading } = useAcl();
    const [sidebarToggleCount, setSidebarToggleCount] = useState(0);
    const [reducedSince, setReducedSince] = useState(null);
    const { agenceActive, agences, peutChanger, changerAgence } = useAgence();

    useEffect(() => {
        fetch('/version.json?t=' + Date.now())
            .then(r => r.json())
            .then(data => setAppVersion(data.version))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!currentUser?.isSuperAdmin) return;

        const fetchCount = () => {
            api.get('/bug_reports?pagination=false')
                .then(res => {
                    const reports = res.data.member || [];
                    const newBugs = reports.filter(r => r.statut === 'nouveau').length;
                    setBugCount(newBugs);
                })
                .catch(() => {});
        };

        fetchCount();
        const interval = setInterval(fetchCount, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [currentUser]);

    return (
        <div style={{
            width: expanded ? '200px' : '56px',
            height: '100vh',
            position: 'sticky',
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1a1a2e',
            transition: 'width 0.2s ease',
            flexShrink: 0,
            zIndex: 100,
        }}>
            {/* Logo + toggle */}
            <div
                onClick={() => {
                    const newExpanded = !expanded;
                    setExpanded(newExpanded);

                    // Architecte
                    if (!newExpanded) {
                        const newCount = sidebarToggleCount + 1;
                        setSidebarToggleCount(newCount);
                        if (newCount >= 10) checkAndUnlock && checkAndUnlock('architecte');
                        setReducedSince(Date.now());
                    } else {
                        // Minimaliste — sidebar réduite 10 min
                        if (reducedSince && Date.now() - reducedSince >= 10 * 60 * 1000) {
                            checkAndUnlock && checkAndUnlock('minimaliste');
                        }
                        setReducedSince(null);
                    }
                }}
                style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    minHeight: '56px',
                    flexShrink: 0,
                }}>
                <svg width="20" height="20" viewBox="0 0 60 60" style={{flexShrink: 0}}>
                    <circle cx="30" cy="30" r="30" fill="#4f46e5"/>
                    <g transform="translate(30,30)">
                        <rect x="-2.3" y="-10.5" width="4.6" height="21" rx="2.3" fill="white"/>
                        <rect x="-10.5" y="-2.3" width="21" height="4.6" rx="2.3" fill="white"/>
                        <path d="M6.5 6.5 Q10.5 9 12.5 12.5" fill="none" stroke="white" strokeWidth="1.8"
                              strokeDasharray="1.8 2.5" strokeLinecap="round"/>
                        <circle cx="12.5" cy="12.5" r="3" fill="white"/>
                    </g>
                </svg>
                {expanded && (
                    <span style={{color: 'white', fontWeight: '700', fontSize: '15px', whiteSpace: 'nowrap'}}>Planning
                        {appVersion && <span style={{fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginLeft: '6px'}}>v{appVersion}</span>}</span>
                )}
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.4)', marginLeft: 'auto', fontSize: '12px'}}>◀</span>
                )}
            </div>

            {/* Agence active */}
            {expanded && agenceActive && (
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
                    {peutChanger && agences.length > 1 ? (
                        <select
                            value={agenceActive.id}
                            onChange={e => changerAgence(e.target.value)}
                            style={{
                                width: '100%', padding: '6px 8px', borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                backgroundColor: 'rgba(255,255,255,0.08)',
                                color: 'white', fontSize: '13px', cursor: 'pointer',
                            }}
                        >
                            {agences.map(a => (
                                <option key={a.id} value={a.id} style={{ color: '#1a1a2e' }}>{a.nom}</option>
                            ))}
                        </select>
                    ) : (
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                            <i className="ti ti-building" style={{ marginRight: '6px' }} />{agenceActive.nom}
                        </div>
                    )}
                </div>
            )}


            {/* Menu items groupés */}
            <nav style={{ flex: 1, paddingTop: '8px', overflowY: 'auto' }}>
                {aclLoading ? (
                    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[1,2,3,4,5].map(i => (
                            <div key={i} style={{
                                height: '36px', borderRadius: '8px',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }} />
                        ))}
                    </div>
                ) : menuGroups.map(group => {
                    // Filtre les items selon les droits
                    const visibleItems = group.items.filter(item => {
                        if (item.superAdminOnly && !currentUser?.isSuperAdmin) return false;
                        if (item.permission && !can(item.permission)) return false;
                        return true;
                    });

                    // Cache le groupe si aucun item visible
                    if (visibleItems.length === 0) return null;
                    if (group.superAdminOnly && !currentUser?.isSuperAdmin) return null;

                    return (
                        <div key={group.label}>
                            {/* Label du groupe */}
                            {expanded && (
                                <div style={{
                                    padding: '10px 16px 4px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    color: 'rgba(255,255,255,0.3)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                }}>
                                    {group.label}
                                </div>
                            )}
                            {visibleItems.map(item => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        if (item.id === 'bug-reports') {
                                            localStorage.setItem('bug_reports_last_visit', new Date().toISOString());
                                            setBugCount(0);
                                        }
                                        onNavigate(item.id);
                                    }}
                                    title={!expanded ? item.label : ''}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '10px 16px',
                                        cursor: 'pointer',
                                        borderLeft: activePage === item.id ? `3px solid var(--accent-color)` : '3px solid transparent',
                                        backgroundColor: activePage === item.id ? 'rgba(79,70,229,0.3)' : 'transparent',
                                        transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={e => {
                                        if (activePage !== item.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                                    }}
                                    onMouseLeave={e => {
                                        if (activePage !== item.id) e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <i className={`ti ${item.icon}`} style={{ fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)' }} />
                                    {expanded && (
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>{item.label}
                                            {item.id === 'bug-reports' && bugCount > 0 && (
                                                <span style={{
                                                    backgroundColor: '#ef4444',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    fontWeight: '700',
                                                    padding: '1px 6px',
                                                    borderRadius: '10px',
                                                    lineHeight: '16px',
                                                }}>{bugCount}</span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </nav>

            {/* Paramètres */}
            <div
                onClick={() => onNavigate('settings')}
                title={!expanded ? 'Paramètres' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    borderLeft: activePage === 'settings' ? `3px solid var(--accent-color)` : '3px solid transparent',
                    backgroundColor: activePage === 'settings' ? 'rgba(79,70,229,0.3)' : 'transparent',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => { if (activePage !== 'settings') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { if (activePage !== 'settings') e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
                <i className="ti ti-settings" style={{ fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)' }} />
                {expanded && <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Paramètres</span>}
            </div>

            {/* Hauts faits — juste avant Nouveautés */}
            <div
                onClick={() => onNavigate('achievements')}
                title={!expanded ? 'Hauts faits' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderLeft: activePage === 'achievements' ? `3px solid var(--accent-color)` : '3px solid transparent',
                    backgroundColor: activePage === 'achievements' ? 'rgba(79,70,229,0.3)' : 'transparent',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => {
                    if (activePage !== 'achievements') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                }}
                onMouseLeave={e => {
                    if (activePage !== 'achievements') e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <i className="ti ti-trophy" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Hauts faits</span>
                )}
            </div>

            {currentUser?.isSuperAdmin && (
                <div
                    onClick={() => onNavigate('achievements-wiki')}
                    title={!expanded ? 'Wiki hauts faits' : ''}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        borderLeft: activePage === 'achievements-wiki' ? `3px solid var(--accent-color)` : '3px solid transparent',
                        backgroundColor: activePage === 'achievements-wiki' ? 'rgba(79,70,229,0.3)' : 'transparent',
                        flexShrink: 0,
                    }}
                    onMouseEnter={e => {
                        if (activePage !== 'achievements-wiki') e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={e => {
                        if (activePage !== 'achievements-wiki') e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    <i className="ti ti-book" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                    {expanded && (
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Wiki hauts faits</span>
                    )}
                </div>
            )}

            {/* Déconnexion — toujours en bas */}
            <div
                onClick={onLogout}
                title={!expanded ? 'Déconnexion' : ''}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                <i className="ti ti-logout" style={{fontSize: '18px', flexShrink: 0, color: 'rgba(255,255,255,0.85)'}}/>
                {expanded && (
                    <span style={{color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Déconnexion</span>
                )}
            </div>
            {showPatchNotes && <PatchNotesModal onClose={() => setShowPatchNotes(false)} />}
        </div>
    );
}